import type { HTMLAttributes, ReactNode } from "react";

import { Heading, Text } from "./Typography";

type Space = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12;

function withClassName(
  baseClasses: string[],
  className: string | undefined,
): string {
  if (className) {
    baseClasses.push(className);
  }

  return baseClasses.join(" ");
}

export interface StackProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
  /**
   * Pass the §4b.3 flex-height chain through to the last child instead of
   * block-growing (the `.marker-tabs__panel--fill` shape). Use it for a Stack
   * that sits between a `Page variant="workspace"` and a fill-mode region:
   * the default grid Stack breaks that chain and leaves the fill table
   * content-grown.
   */
  fill?: boolean;
  gap?: Space;
}

export function Stack({
  children,
  className,
  fill = false,
  gap = 4,
  ...props
}: StackProps) {
  const classes = ["marker-stack", `marker-stack--gap-${String(gap)}`];
  if (fill) {
    classes.push("marker-stack--fill");
  }

  return (
    <div className={withClassName(classes, className)} {...props}>
      {children}
    </div>
  );
}

type InlineAlign = "start" | "center" | "end" | "baseline" | "stretch";
type InlineJustify = "start" | "center" | "end" | "between";

export interface InlineProps extends HTMLAttributes<HTMLDivElement> {
  align?: InlineAlign;
  children: ReactNode;
  gap?: Space;
  justify?: InlineJustify;
}

export function Inline({
  align = "center",
  children,
  className,
  gap = 3,
  justify,
  ...props
}: InlineProps) {
  const inlineClasses = [
    "marker-inline",
    `marker-inline--align-${align}`,
    `marker-inline--gap-${String(gap)}`,
  ];
  if (justify) {
    inlineClasses.push(`marker-inline--justify-${justify}`);
  }

  return (
    <div className={withClassName(inlineClasses, className)} {...props}>
      {children}
    </div>
  );
}

// "2-3" is the UI-LAW §4b.5 responsive chart grid: 2-up, 3-up ≥1440px.
type GridColumns = 1 | 2 | 3 | 4 | "2-3";
// "full" spans the whole row (grid-column: 1 / -1) at any column count —
// required under responsive column counts where a numeric span drifts.
type GridSpan = 1 | 2 | 3 | 4 | "full";

export interface GridProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
  columns?: GridColumns;
  gap?: Space;
}

export function Grid({
  children,
  className,
  columns = 2,
  gap = 4,
  ...props
}: GridProps) {
  return (
    <div
      className={withClassName(
        [
          "marker-grid",
          `marker-grid--${String(columns)}`,
          `marker-grid--gap-${String(gap)}`,
        ],
        className,
      )}
      {...props}
    >
      {children}
    </div>
  );
}

export interface GridItemProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
  span?: GridSpan;
}

export function GridItem({
  children,
  className,
  span,
  ...props
}: GridItemProps) {
  const classes = ["marker-grid__item"];
  if (span) {
    classes.push(`marker-grid__item--span-${String(span)}`);
  }

  return (
    <div className={withClassName(classes, className)} {...props}>
      {children}
    </div>
  );
}

export interface SectionProps extends HTMLAttributes<HTMLElement> {
  actions?: ReactNode;
  children: ReactNode;
  description?: ReactNode;
  hairline?: boolean;
  heading?: ReactNode;
  /** A `HelpTip` rendered beside the heading (UI-LAW §4d). */
  help?: ReactNode;
}

export function Section({
  actions,
  children,
  className,
  description,
  hairline = false,
  heading,
  help,
  ...props
}: SectionProps) {
  const classes = ["marker-section"];
  if (hairline) {
    classes.push("marker-section--hairline");
  }

  return (
    <section className={withClassName(classes, className)} {...props}>
      {heading || description || actions || help ? (
        <div className="marker-section__header">
          <div>
            {/* A section can carry its disclosure without a heading: where the
                content already names itself, the `i` is the only chrome the
                header row needs. */}
            {heading || help ? (
              <span className="marker-section__heading-row">
                {heading ? (
                  <Heading level={2} size="sm">
                    {heading}
                  </Heading>
                ) : null}
                {help}
              </span>
            ) : null}
            {description ? (
              <Text className="marker-section__description" variant="muted">
                {description}
              </Text>
            ) : null}
          </div>
          {actions ? <Inline gap={3}>{actions}</Inline> : null}
        </div>
      ) : null}
      {children}
    </section>
  );
}

export type DividerProps = HTMLAttributes<HTMLHRElement>;

export function Divider({ className, ...props }: DividerProps) {
  return (
    <hr className={withClassName(["marker-divider"], className)} {...props} />
  );
}
